import { Role } from './../models/api/dsm-types';
import { Injectable } from '@angular/core';
import {
  Router,
  UrlTree,
  RouterStateSnapshot,
  Params,
  ActivatedRouteSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AuthUrlService } from '../services/auth-url.service';
import { UserService } from '../services/user.service';
import { SessionService } from '../services/session.service';
import { TokenIntrospectionResponse } from '@core/models/api/response/oauth-response.model';
import { LogService } from '@core/services/log.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard {
  constructor(
    private authUrlService: AuthUrlService,
    private router: Router,
    private userService: UserService,
    private sessionService: SessionService,
    private window: Window,
    private logService: LogService
  ) {}

  parseQueryParams(route: string): [string, { [key: string]: string }] {
    const [url, params] = route.split('?');
    if (!params) {
      return [url, {}];
    }

    const queryParams = params
      .split('&')
      .reduce((parms: { [key: string]: string }, kv) => {
        const [key, value] = kv.split('=');
        parms[key] = value;
        return parms;
      }, {});

    return [url, queryParams];
  }

  canActivateChild(
    route: ActivatedRouteSnapshot
  ): boolean | Observable<boolean> | UrlTree | Observable<boolean | UrlTree> {
    const routerState: RouterStateSnapshot = this.router.routerState.snapshot;
    const transformedParams = this.authUrlService.parseRoute(route);
    let jwt = this.sessionService.fetchJwt();
    let accessToken = this.sessionService.fetchAccessToken();
    let refreshToken = this.sessionService.fetchRefreshToken();
    let role = this.sessionService.fetchRole();
    let verifier = this.sessionService.fetchVerifier();

    const requestedRoute = this.window.location?.pathname || '';
    const params: Params = {
      requestedRoute,
      ...route.queryParams,
    };

    if (accessToken && jwt) {
      if (
        this.sessionService.validateTokenLifetime(accessToken, jwt) ===
        accessToken
      ) {
        const channelFromStorage =
          this.window.localStorage.getItem('agencyChannel');
        this.sessionService.storeJwt(jwt);
        this.sessionService.storeToken(accessToken, 'access_token');
        this.sessionService.updateToken(accessToken, 'access_token');
        if (params.userType) {
          this.sessionService.setUserType(params.userType);
        }
        if (transformedParams.agencyChannel) {
          role = transformedParams.agencyChannel;
          if (role && channelFromStorage && role !== channelFromStorage) {
            this.logService.debug(
              `agency channel mismatch: route ${role} != storage ${channelFromStorage}`
            );
            this.window.localStorage.removeItem('agencyChannel');
          }
          if (role) {
            this.sessionService.storeRole(role);
          }
        } else if (channelFromStorage) {
          this.sessionService.storeRole(channelFromStorage);
        }

        if (role) {
          this.userService.updateUser({
            role: role as Role,
          });
        }
        this.userService.populateJwtAndTokenIntrospectInfo(
          {},
          jwt as TokenIntrospectionResponse
        );
        return true;
      } else {
        if (refreshToken) {
          return this.authUrlService.exchangeRefreshToken(refreshToken).pipe(
            take(1),
            switchMap(({ accessToken, refreshToken }) => {
              if (accessToken && refreshToken) {
                return this.sessionService
                  .fetchTokenIntrospectionWithAccessToken(accessToken)
                  .pipe(
                    take(1),
                    map((tokenInfo) => {
                      if (tokenInfo) {
                        this.sessionService.storeJwt(tokenInfo);
                        this.userService.populateJwtAndTokenIntrospectInfo(
                          jwt,
                          tokenInfo
                        );
                        if (
                          this.sessionService.validateTokenLifetime(
                            accessToken,
                            tokenInfo
                          ) === accessToken
                        ) {
                          return true;
                        }
                      }
                      this.userService.logout();
                      return false;
                    })
                  );
              } else {
                this.userService.logout();
                return of(false);
              }
            })
          );
        } else {
          this.userService.logout();
          this.logService.debug('Access token expired and no refresh token');
          return false;
        }
      }
    }

    if (transformedParams.agencyChannel) {
      role = transformedParams.agencyChannel;
      const channelStorage = this.window.localStorage.getItem('agencyChannel');
      if (role && channelStorage && role !== channelStorage) {
        this.logService.debug(
          `agency channel mismatch: route ${role} != storage ${channelStorage}`
        );
        this.window.localStorage.removeItem('agencyChannel');
      }
      if (role) {
        this.sessionService.storeRole(role);
      }
    }

    if (role) {
      this.userService.updateUser({
        role: role as Role,
      });
    }

    if (accessToken !== null) {
      this.sessionService.updateToken(accessToken, 'access_token');
    }

    if (route.queryParams?.error) {
      // Authentication failed
      // TODO: implement ErrorSettableComponent or use some other auth error strategy
      this.window.localStorage.removeItem('agencyChannel');
      return false;
    }

    const authCode = route.queryParams['code'];

    if (!accessToken && verifier && authCode) {
      if (transformedParams.agencyChannel) {
        role = transformedParams.agencyChannel;
        if (role) {
          this.sessionService.storeRole(role);
        }
      }

      return this.authUrlService.exchangeAuthorizationCode(authCode).pipe(
        take(1),
        switchMap(({ accessToken, jwt }) => {
          if (accessToken && jwt) {
            return this.sessionService
              .fetchTokenIntrospectionWithAccessToken(accessToken)
              .pipe(
                take(1),
                map((tokenInfo) => {
                  if (tokenInfo) {
                    this.sessionService.storeJwt(tokenInfo);
                    this.userService.populateJwtAndTokenIntrospectInfo(
                      jwt,
                      tokenInfo
                    );
                    if (
                      this.sessionService.validateTokenLifetime(
                        accessToken,
                        tokenInfo
                      ) === accessToken
                    ) {
                      if (transformedParams.requestedRoute) {
                        const [requested, queryParams] = this.parseQueryParams(
                          transformedParams.requestedRoute
                        );
                        return this.router.createUrlTree([requested], {
                          queryParams,
                        });
                      } else {
                        return true;
                      }
                    } else {
                      return this.router.createUrlTree(['/splash'], {
                        queryParams: params,
                      });
                    }
                  }
                  return false;
                })
              );
          }
          return of(false);
        })
      );
    }

    // Needs authenticated
    return this.userService.getUser().pipe(
      take(1),
      map((user) => {
        const agencyChannel = user?.role;
        if (agencyChannel) {
          // Agency Channel is known, go straight to auth redirect
          this.authUrlService.performAuthRedirect(
            agencyChannel,
            route,
            routerState
          );
          return false;
        }

        const channelFromStorage =
          this.window.localStorage.getItem('agencyChannel');
        if (channelFromStorage) {
          this.sessionService.storeRole(channelFromStorage);
          this.authUrlService.performAuthRedirect(
            channelFromStorage,
            route,
            routerState
          );
          return false;
        }
        return this.router.createUrlTree(['/splash'], {
          queryParams: params,
        });
      })
    );
  }
}
